/**
 * API functions for Doctors
 * GET /doctors/by-department/:departmentId?isActive=true
 * POST /doctors/list
 * POST /doctors - create doctor
 * GET /doctors/:doctorId - get by id
 * PATCH /doctors/update/:doctorId - update doctor
 * POST /doctors/disable - disable doctor
 * POST /doctors/enable - enable doctor
 */

import { apiClient } from "./auth";
import { fetchAllListPages } from "./cursorPagination";
import type {
  GetDoctorsByDepartmentResponse,
  DoctorItem,
  DoctorsListResponse,
  DoctorListItem,
  DoctorProfile,
} from "../Interfaces/DoctorInterface";

export interface CreateDoctorPayload {
  email: string;
  phone: string;
  /** Full name of the doctor, taken from the Name field in the form */
  fullName: string;
  /** Selected facility/unit IDs (top-level for backend; tenant admins use this to assign doctor to facility) */
  facilityIds?: string[];
  doctorProfile: {
    doctorCode: string;
    registrationNo: string;
    departmentIds: string[];
    facilityIds?: string[];
    designation: string;
    specialtyTags: string[];
    opdAvailable: boolean;
    ipdAvailable: boolean;
    isSurgeon: boolean;
    isVisiting: boolean;
    languages: string[];
    isActive: boolean;
  };
}

export interface CreateDoctorResponse {
  id: string;
  tenantId: string;
  email: string;
  phone: string;
  doctorProfile: unknown;
  createdAt: string;
}

export interface GetDoctorsListParams {
  limit?: number;
  cursor?: string;
  departmentId?: string;
  facilityId?: string;
  isActive?: boolean;
}

/** Response from GET /doctors/:doctorId (backend returns id) */
export interface GetDoctorByIdResponse {
  id: string;
  tenantId: string;
  fullName?: string;
  email: string;
  phone: string;
  doctorProfile: DoctorProfile;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Payload for PATCH /doctors/update/:doctorId */
export interface UpdateDoctorPayload {
  email?: string;
  phone?: string;
  fullName?: string;
  doctorProfile?: {
    designation?: string;
    specialtyTags?: string[];
    languages?: string[];
    isActive?: boolean;
  };
  isActive?: boolean;
}

/** Response from PATCH /doctors/update/:doctorId */
export interface UpdateDoctorResponse {
  id: string;
  tenantId: string;
  email: string;
  phone: string;
  doctorProfile: unknown;
  isActive: boolean;
  updatedAt: string;
}

/**
 * Search doctors by name (and optional facility/department).
 * GET /doctors/search?name=...&facilityId=... (query params are dynamic).
 * Backend requires name with at least 2 characters.
 */
export interface SearchDoctorsParams {
  name: string;
  facilityId?: string;
  departmentId?: string;
  isActive?: boolean;
  limit?: number;
  cursor?: string;
}

export interface SearchDoctorsResponse {
  doctors: Array<{
    id: string;
    tenantId: string;
    fullName?: string;
    email: string;
    phone: string;
    doctorProfile: DoctorProfile;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  pagination?: { nextCursor?: string };
}

export async function searchDoctorsApi(
  params: SearchDoctorsParams
): Promise<SearchDoctorsResponse> {
  const searchParams = new URLSearchParams();
  if (params.name?.trim()) searchParams.set("name", params.name.trim());
  if (params.facilityId) searchParams.set("facilityId", params.facilityId);
  if (params.departmentId) searchParams.set("departmentId", params.departmentId);
  if (params.isActive !== undefined) searchParams.set("isActive", String(params.isActive));
  if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
  if (params.cursor) searchParams.set("cursor", params.cursor);
  const url = `/doctors/search?${searchParams.toString()}`;
  const response = await apiClient.get<SearchDoctorsResponse>(url);
  return response.data;
}

/** Params for GET /doctors/by-department/:departmentId */
export interface GetDoctorsByDepartmentParams {
  /**
   * Narrows doctors to ones attached to this facility (a doctor can be linked to
   * multiple departments/facilities). When omitted, tenant/super admins see doctors
   * across all facilities; facility-scoped roles are auto-restricted server-side.
   */
  facilityId?: string;
  isActive?: boolean;
  limit?: number;
  cursor?: string;
}

/**
 * Agent-accessible doctor lookup by department (read-only).
 * GET /doctors/by-department/:departmentId/lookup?facilityId=...&isActive=true
 * Requires OPPORTUNITIES:VIEW_DETAILS or PATIENTS_CONTACTS:VIEW_DETAILS.
 */
export async function getDoctorsByDepartmentLookupApi(
  departmentId: string,
  params: GetDoctorsByDepartmentParams = {}
): Promise<GetDoctorsByDepartmentResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("isActive", String(params.isActive ?? true));
  if (params.facilityId) searchParams.set("facilityId", params.facilityId);
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.cursor) searchParams.set("cursor", params.cursor);
  const response = await apiClient.get<GetDoctorsByDepartmentResponse>(
    `/doctors/by-department/${departmentId}/lookup?${searchParams.toString()}`
  );
  return response.data;
}

/**
 * Fetch all doctors for a department via agent lookup endpoint.
 */
export async function fetchAllDoctorsByDepartmentLookupApi(
  departmentId: string,
  params: Omit<GetDoctorsByDepartmentParams, "cursor"> = {}
): Promise<DoctorItem[]> {
  const limit = params.limit ?? 200;
  const results: DoctorItem[] = [];
  let cursor: string | undefined;
  do {
    const res = await getDoctorsByDepartmentLookupApi(departmentId, {
      ...params,
      limit,
      cursor,
    });
    results.push(...(res.doctors ?? []));
    cursor = getNextCursorFromDoctorsByDepartment(res);
  } while (cursor);
  return results;
}

/**
 * Fetch doctors by department. Used to populate the doctor dropdown when creating an opportunity.
 * GET /doctors/by-department/:departmentId?facilityId=...&isActive=true
 */
export async function getDoctorsByDepartmentApi(
  departmentId: string,
  params: GetDoctorsByDepartmentParams = {}
): Promise<GetDoctorsByDepartmentResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("isActive", String(params.isActive ?? true));
  if (params.facilityId) searchParams.set("facilityId", params.facilityId);
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.cursor) searchParams.set("cursor", params.cursor);
  const response = await apiClient.get<GetDoctorsByDepartmentResponse>(
    `/doctors/by-department/${departmentId}?${searchParams.toString()}`
  );
  return response.data;
}

/** Resolve the next cursor from a by-department response (supports pagination or legacy flat cursor). */
function getNextCursorFromDoctorsByDepartment(
  res: GetDoctorsByDepartmentResponse
): string | undefined {
  if (res.pagination) {
    return res.pagination.nextCursor && res.pagination.hasMore !== false
      ? res.pagination.nextCursor
      : undefined;
  }
  return res.nextCursor ?? undefined;
}

/**
 * Fetch all doctors for a department across cursor pages.
 * GET /doctors/by-department/:departmentId
 */
export async function fetchAllDoctorsByDepartmentApi(
  departmentId: string,
  params: Omit<GetDoctorsByDepartmentParams, "cursor"> = {}
): Promise<DoctorItem[]> {
  const limit = params.limit ?? 200;
  const results: DoctorItem[] = [];
  let cursor: string | undefined;
  do {
    const res = await getDoctorsByDepartmentApi(departmentId, {
      ...params,
      limit,
      cursor,
    });
    results.push(...(res.doctors ?? []));
    cursor = getNextCursorFromDoctorsByDepartment(res);
  } while (cursor);
  return results;
}

/**
 * Fetch doctors list. POST /doctors/list
 * Supports optional departmentId, facilityId, cursor, isActive. Backend caps limit (max 500).
 */
export async function getDoctorsListApi(
  params?: GetDoctorsListParams
): Promise<DoctorsListResponse> {
  const requestBody: Record<string, unknown> = {
    limit: params?.limit ?? 100,
  };
  if (params?.cursor != null) requestBody.cursor = params.cursor;
  if (params?.departmentId != null) requestBody.departmentId = params.departmentId;
  if (params?.facilityId != null) requestBody.facilityId = params.facilityId;
  if (params?.isActive != null) requestBody.isActive = params.isActive;
  const response = await apiClient.post<Record<string, unknown>, DoctorsListResponse>(
    "/doctors/list",
    requestBody
  );
  return response.data;
}

/**
 * Fetch all doctors across cursor pages. POST /doctors/list
 */
export async function fetchAllDoctorsList(
  params?: Omit<GetDoctorsListParams, "cursor">
): Promise<DoctorListItem[]> {
  const limit = Math.min(params?.limit ?? 100, 500);
  return fetchAllListPages((cursor) =>
    getDoctorsListApi({ ...params, limit, cursor })
  );
}

/**
 * Create a doctor. POST /doctors
 * Backend adds tenantId and facilityIds from authenticated user.
 */
export async function createDoctorApi(
  payload: CreateDoctorPayload
): Promise<CreateDoctorResponse> {
  const response = await apiClient.post<CreateDoctorPayload, CreateDoctorResponse>(
    "/doctors",
    payload
  );
  return response.data;
}

/**
 * Get doctor by ID. GET /doctors/:doctorId
 */
export async function getDoctorByIdApi(
  doctorId: string
): Promise<GetDoctorByIdResponse> {
  const response = await apiClient.get<GetDoctorByIdResponse>(
    `/doctors/${doctorId}`
  );
  return response.data;
}

/**
 * Update doctor. PATCH /doctors/update/:doctorId
 */
export async function updateDoctorApi(
  doctorId: string,
  payload: UpdateDoctorPayload
): Promise<UpdateDoctorResponse> {
  const response = await apiClient.patch<UpdateDoctorPayload, UpdateDoctorResponse>(
    `/doctors/update/${doctorId}`,
    payload
  );
  return response.data;
}

/**
 * Disable doctor. POST /doctors/disable
 */
export async function disableDoctorApi(doctorId: string): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ doctorId: string }, { success: boolean }>(
    "/doctors/disable",
    { doctorId }
  );
  return response.data;
}

/**
 * Enable doctor. POST /doctors/enable
 */
export async function enableDoctorApi(doctorId: string): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ doctorId: string }, { success: boolean }>(
    "/doctors/enable",
    { doctorId }
  );
  return response.data;
}
